import type { Board, Dir, FixedCell, PieceKind, RedstoneLevel } from './redstone.ts';

/**
 * Levels are drawn as text so they are easy to review:
 *   .  empty      S  source      L  lever (ids L1, L2… in reading order)
 *   G  goal lamp (must be lit)   X  forbidden lamp (must stay dark)   #  stone (blocked)
 *   -  fixed wire
 * Solutions use the same coordinates: x from the left, y from the top, both from 0.
 */
function parse(rows: string[]) {
  const fixed: Record<string, FixedCell> = {};
  let levers = 0;
  rows.forEach((row, y) =>
    [...row].forEach((ch, x) => {
      const k = `${x},${y}`;
      if (ch === 'S') fixed[k] = { kind: 'source' };
      else if (ch === 'L') fixed[k] = { kind: 'lever', id: `L${++levers}` };
      else if (ch === 'G') fixed[k] = { kind: 'lamp', goal: true };
      else if (ch === 'X') fixed[k] = { kind: 'lamp', goal: false };
      else if (ch === '#') fixed[k] = { kind: 'block' };
      else if (ch === '-') fixed[k] = { kind: 'piece', piece: 'wire', dir: 0 };
      else if (ch !== '.') throw new Error(`unknown map character ${ch}`);
    }),
  );
  return { width: rows[0]!.length, height: rows.length, fixed };
}

const N: Dir = 0;
const E: Dir = 1;
const w = (x: number, y: number) => ({ x, y, piece: 'wire' as PieceKind, dir: 0 as Dir });
const g = (piece: PieceKind, x: number, y: number, dir: Dir) => ({ x, y, piece, dir });
const sol = (placements: Board['placements'], levers: Record<string, boolean> = {}): Board => ({
  placements,
  levers,
});

export const REDSTONE_LEVELS: RedstoneLevel[] = [
  {
    id: 'first-spark',
    version: 1,
    title: 'First spark',
    goal: 'Lay wire from the power source to the lamp.',
    hints: [
      'A source powers the cells right next to it.',
      'Wires that touch become one wire, all on or all off.',
    ],
    ...parse(['.....', 'S...G', '.....']),
    supply: { wire: 4 },
    budget: 3,
    solution: sol([w(1, 1), w(2, 1), w(3, 1)]),
  },
  {
    id: 'mind-the-lamp',
    version: 1,
    title: 'Mind the red lamp',
    goal: 'Light the gold lamp, but keep the red lamp dark. The lever starts off.',
    hints: [
      'Tap the lever to switch it on.',
      'A powered wire also powers anything next to it — go around the red lamp.',
    ],
    ...parse(['..X..', 'L...G', '.....']),
    supply: { wire: 6 },
    budget: 5,
    solution: sol([w(0, 2), w(1, 2), w(2, 2), w(3, 2), w(4, 2)], { L1: true }),
  },
  {
    id: 'one-way-street',
    version: 1,
    title: 'One-way street',
    goal: 'Get power past the red lamp without lighting it.',
    hints: [
      'A repeater only powers the cell its arrow points at.',
      'Put the repeater right above the red lamp, pointing at the gold lamp.',
    ],
    ...parse(['.......', 'S.....G', '...X...']),
    supply: { wire: 4, repeater: 1 },
    budget: 5,
    solution: sol([w(1, 1), w(2, 1), g('repeater', 3, 1, E), w(4, 1), w(5, 1)]),
  },
  {
    id: 'something-from-nothing',
    version: 1,
    title: 'Something from nothing',
    goal: 'There is no power source. Light the lamp anyway.',
    hints: [
      'A torch is on when nothing powers it from behind.',
      'Point the torch at a wire that leads to the lamp.',
    ],
    ...parse(['#####', '....G', '#####']),
    supply: { not: 1, wire: 3 },
    budget: 2,
    solution: sol([g('not', 2, 1, E), w(3, 1)]),
  },
  {
    id: 'double-detour',
    version: 1,
    title: 'Double detour',
    goal: 'Get power past both red lamps to the gold lamp.',
    hints: [
      'Not enough wire to go the long way round.',
      'Each red lamp needs its own repeater right below it.',
    ],
    ...parse(['...X...X.', 'S.......G', '.........']),
    supply: { wire: 5, repeater: 2 },
    budget: 7,
    solution: sol([
      w(1, 1),
      w(2, 1),
      g('repeater', 3, 1, E),
      w(4, 1),
      w(5, 1),
      w(6, 1),
      g('repeater', 7, 1, E),
    ]),
  },
  {
    id: 'both-at-once',
    version: 1,
    title: 'Both at once',
    goal: 'Use a Both piece to light the lamp. Try one lever, then both.',
    hints: [
      'A Both piece is only on when the squares on both of its sides are powered.',
      'Put it between the two levers with its arrow pointing at the lamp, then switch both levers on.',
    ],
    ...parse(['..L..', '....G', '..L..']),
    supply: { and: 1, wire: 1 },
    budget: 2,
    solution: sol([g('and', 2, 1, E), w(3, 1)], { L1: true, L2: true }),
  },
  {
    id: 'either-one',
    version: 1,
    title: 'Either one',
    goal: 'Light the gold lamp with an Either piece and keep the red lamp dark.',
    hints: [
      'An Either piece is on when one side or the other is powered, so one lever is enough.',
      'Wire between the levers would light the red lamp. Put the Either there instead, arrow to the lamp.',
    ],
    ...parse(['..L..', '.X..G', '..L..']),
    supply: { or: 1, wire: 1 },
    budget: 2,
    solution: sol([g('or', 2, 1, E), w(3, 1)], { L1: true }),
  },
  {
    id: 'so-close',
    version: 1,
    title: 'So close',
    goal: 'The power is right there, but wire would light the red lamp. Light the gold one.',
    hints: [
      'An Either piece next to the gold lamp only needs one side powered. The red lamp side never counts.',
      'A Torch is on when nothing powers it from behind. Put one under the Either, pointing up.',
    ],
    ...parse(['....X#', '...S.G', '.....#']),
    supply: { or: 1, not: 1, wire: 2 },
    budget: 2,
    solution: sol([g('or', 4, 1, E), g('not', 4, 2, N)]),
  },
  {
    id: 'long-way-round',
    version: 1,
    title: 'The long way round',
    goal: 'Bring power from both levers to a Both piece by the gold lamp, past the red lamps.',
    hints: [
      'The Both piece goes right next to the gold lamp, with a wire coming in on each side.',
      'The top road passes a red lamp. A Repeater gets past it without lighting it.',
    ],
    ...parse(['.L.....', '...X..#', '....X.G', '......#', '....L..']),
    supply: { wire: 7, repeater: 1, and: 1 },
    budget: 8,
    solution: sol(
      [
        w(2, 0),
        g('repeater', 3, 0, E),
        w(4, 0),
        w(5, 0),
        w(5, 1),
        g('and', 5, 2, E),
        w(5, 3),
        w(5, 4),
      ],
      { L1: true, L2: true },
    ),
  },
  {
    id: 'grand-relay',
    version: 1,
    title: 'Grand relay',
    goal: 'Use everything you know: light the gold lamp and keep every red lamp dark.',
    hints: [
      'An Either piece next to the gold lamp can listen to the fixed wire below it.',
      'That fixed wire needs a Both piece under it, fed by both levers. Use the Repeater between the red lamps.',
    ],
    ...parse(['.....X#', '....#.G', '..X.#-#', 'L.....L', '..X..X.']),
    supply: { wire: 4, repeater: 1, and: 1, or: 1 },
    budget: 6,
    solution: sol(
      [w(1, 3), g('repeater', 2, 3, E), w(3, 3), w(4, 3), g('and', 5, 3, N), g('or', 5, 1, E)],
      { L1: true, L2: true },
    ),
  },
];
